import { Link as RouterLink } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

// project imports
import { useTranslation } from 'contexts/LanguageContext';

// assets
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import loginHero from 'assets/images/auth/login-hero.jpg';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Grid
      container
      spacing={0}
      sx={{
        width: '100%',
        minHeight: { xs: 'auto', md: '100%' },
        height: { md: '100%' },
        alignContent: { xs: 'flex-start', md: 'stretch' },
        overflow: { xs: 'visible', md: 'hidden' }
      }}
    >
      <Grid
        size={{ xs: 12, md: 5, lg: 4 }}
        sx={{
          order: { xs: 2, md: 1 },
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          px: { xs: 2, sm: 4, md: 6, lg: 8 },
          py: { xs: 2, md: 4 },
          minHeight: { xs: 'auto', md: 0 },
          overflow: { xs: 'visible', md: 'auto' }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 3, sm: 4 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={0.75} sx={{ mb: 0.5 }}>
              <Typography variant="h3">{t('Login')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('Sign in to manage BHS hotel reservations.')}
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel
                    htmlFor="email"
                  >
                    {t('Email')}
                  </InputLabel>
                  <OutlinedInput id="email" type="email" label={t('Email')} fullWidth />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel
                    htmlFor="password"
                  >
                    {t('Password')}
                  </InputLabel>
                  <OutlinedInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label={t('Password')}
                    fullWidth
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? t('Hide password') : t('Show password')}
                          color="secondary"
                          onClick={() => setShowPassword((value) => !value)}
                          edge="end"
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: -1 }}>
                  <Link component="button" type="button" variant="body1" underline="hover" onClick={() => setForgotPasswordOpen(true)} sx={{ fontSize: '0.9rem', fontWeight: 500, border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer' }}>
                    {t('Forgot password?')}
                  </Link>
                </Stack>
              </Grid>

              <Grid size={12}>
                <Button component={RouterLink} to="/2fa" fullWidth size="large" variant="contained">
                  {t('Login')}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Grid>

      <Grid
        size={{ xs: 12, md: 7, lg: 8 }}
        sx={{
          order: { xs: 1, md: 2 },
          display: 'block',
          height: { xs: 180, sm: 240, md: '100%' },
          minHeight: { xs: 180, sm: 240, md: 0 }
        }}
      >
        <Box
          component="img"
          src={loginHero}
          alt="Business planning workspace"
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            objectPosition: { xs: 'center 42%', md: 'center' },
            borderRadius: 0
          }}
        />
      </Grid>
      <Dialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('Forgot password?')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('Enter your email address and we will send a password reset link.')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel htmlFor="forgot-password-email">{t('Email')}</InputLabel>
              <OutlinedInput id="forgot-password-email" type="email" label={t('Email')} fullWidth autoFocus />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="secondary" variant="outlined" onClick={() => setForgotPasswordOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={() => setForgotPasswordOpen(false)}>
            {t('Send reset link')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
